import React, { useState, useRef, useEffect, useCallback } from 'react';
import Screen1 from './Screen1.js';

// ── S.O.S Terminal — interactive shell for the desktop ────────────────────────

const NEOFETCH = String.raw`
        .;:;.          sean@sos
      .;☘☘☘☘;.         ─────────────────
     ;☘☘☘☘☘☘☘;         OS:       S.O.S v1.0.0 (PhosphorGreen)
      ':☘☘☘☘:'         Kernel:   clover-9.81-rolling
     .;☘☘☘☘☘☘;.        Host:     SeanCore™ Mk.IX
    ;☘☘☘'  '☘☘☘;       Uptime:   7+ years in prod
     ':;'    ';:'      Shell:    sos-sh 4.0
        ||              Display:  PhosphorTech GreenBeam 64
        ||              Theme:    Kelly Green on Black [CRT]
        ''              Memory:   640KB base / 8192MB ext
`;

const HELP = `Available commands:

  help              you are here
  whoami            identify current user
  ls                list desktop contents
  cat <file>        read a file (try: about.txt)
  open <app>        launch an app (about, resume, projects, notes, chat)
  projects          quick project listing
  neofetch          system information
  date              current system time
  echo <text>       repeat after me
  sudo <cmd>        try it and see
  clear             wipe the screen
  exit              close terminal`;

const ABOUT_TXT = `Sean Sutton — Senior Software Engineer / Technical Lead
New Orleans, LA

7+ years shipping cloud migrations, DevOps automation, and
full-stack builds. GGML contributor (the tensor library that
preceded Ollama). Restores classic consoles and 70s stereo
gear when not pushing to prod.

Type 'open resume' for the full dossier.`;

const PROJECTS_TXT = `/dev/projects:

  [1] spirit-mingle    A supernatural dating app (Next.js 15 + Framer Motion)
  [2] thot-leader      Fully automated AI satire pipeline (Claude + DALL-E 3)
  [3] sos-portfolio    This OS. An LLM runs in your browser tab right now.

Type 'open projects' for full case files.`;

const APPS = ['about', 'resume', 'projects', 'notes', 'chat'];
const APP_IDS = { about: 'about', resume: 'resume', projects: 'projects', notes: 'text-editor', chat: 'chat' };

const BANNER = `S.O.S Terminal — sos-sh 4.0 (phosphor build)
Type 'help' to get your bearings.
`;

const Terminal = ({ onClose, position, zIndex, onFocus, focused, onOpenApp }) => {
  const [lines, setLines] = useState([{ type: 'out', text: BANNER }]);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  const print = useCallback((text, type = 'out') => {
    setLines(prev => [...prev, { type, text }]);
  }, []);

  const run = (raw) => {
    const cmdLine = raw.trim();
    setLines(prev => [...prev, { type: 'cmd', text: cmdLine }]);
    if (!cmdLine) return;

    const [cmd, ...args] = cmdLine.split(/\s+/);
    const arg = args.join(' ');

    switch (cmd.toLowerCase()) {
      case 'help':     print(HELP); break;
      case 'whoami':   print('guest — but Sean built the place. ☘'); break;
      case 'ls':
        print(`about.txt   resume.txt   projects/   notes/   chat.app   .secrets`);
        break;
      case 'cat':
        if (arg === 'about.txt') print(ABOUT_TXT);
        else if (arg === 'resume.txt') print(`resume.txt is 4,000 lines of pure phosphor.\nType 'open resume' to view it properly.`);
        else if (arg === '.secrets') print('Permission denied. Nice try though.', 'err');
        else if (!arg) print('usage: cat <file>', 'err');
        else print(`cat: ${arg}: No such file or directory`, 'err');
        break;
      case 'open': {
        const app = arg.toLowerCase();
        if (APP_IDS[app]) {
          print(`Launching ${app}...`);
          onOpenApp(APP_IDS[app]);
        } else {
          print(`open: unknown app '${arg || ''}'. Options: ${APPS.join(', ')}`, 'err');
        }
        break;
      }
      case 'projects': print(PROJECTS_TXT); break;
      case 'neofetch': print(NEOFETCH); break;
      case 'matrix':
        print(`Wrong universe, choom. This is Night City — Irish district.`);
        break;
      case 'date':     print(new Date().toString()); break;
      case 'echo':     print(arg || ''); break;
      case 'sudo':
        print(`guest is not in the sudoers file. This incident will be reported to Sean.`, 'err');
        break;
      case 'rm':
        print(`rm: cannot remove anything: this OS is read-only and emotionally resilient`, 'err');
        break;
      case 'clear':    setLines([]); break;
      case 'exit':     onClose(); break;
      default:
        print(`sos-sh: command not found: ${cmd}. Type 'help'.`, 'err');
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') {
      const value = input;
      setInput('');
      if (value.trim()) {
        setHistory(prev => [...prev, value]);
      }
      setHistIdx(-1);
      run(value);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!history.length) return;
      const idx = histIdx === -1 ? history.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(idx);
      setInput(history[idx]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIdx === -1) return;
      const idx = histIdx + 1;
      if (idx >= history.length) { setHistIdx(-1); setInput(''); }
      else { setHistIdx(idx); setInput(history[idx]); }
    }
  };

  return (
    <Screen1 title="Terminal — sos-sh" onClose={onClose} position={position} zIndex={zIndex} onFocus={onFocus} focused={focused}>
      <div className="terminal" onClick={() => inputRef.current?.focus()}>
        <div className="terminal-output">
          {lines.map((line, i) => (
            <pre key={i} className={`term-line term-${line.type}`}>
              {line.type === 'cmd' ? <><span className="term-prompt">guest@sos:~$</span> {line.text}</> : line.text}
            </pre>
          ))}
          <div className="term-input-row">
            <span className="term-prompt">guest@sos:~$</span>
            <input
              ref={inputRef}
              className="term-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              autoFocus
              spellCheck="false"
              autoComplete="off"
              autoCapitalize="off"
            />
          </div>
          <div ref={bottomRef} />
        </div>
      </div>
    </Screen1>
  );
};

export default Terminal;
